import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Eventcalendar,
  Input,
  localeDe,
  localeEn,
  localeEs,
  localeFr,
  localeNo,
  localeSv,
  localeUa,
  luxonTimezone,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventcalendarView,
  MbscLocale,
  Popup,
} from "@mobiscroll/react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import { DateType } from "@mobiscroll/react/dist/src/core/util/datetime";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { IconButton, LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import { AvailabilityEvent } from "components/calendarEvents/AvailabilityEvent";
import { ProjectTaskEvent } from "components/calendarEvents/ProjectTaskEvent";
import { SalesTaskEvent } from "components/calendarEvents/SalesTaskEvent";
import { addHours } from "date-fns";
import { calculateUrgencyColorHex, DefinedDate, formatDate, isAbortError } from "helpers";
import engineerSettings from "helpers/engineerSettings";
import * as luxon from "luxon";
import {
  ServiceJob,
  TechnicianAvailabilityType,
  TimesType,
  UpdateAvailabilityInputType,
} from "operations/schema/schema";
import { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  getMobileCalendar,
  setCalendarSelectedDate,
  setEditAvailability,
  updateAvailability,
} from "store/slices/calendar.store";
import { setOpen } from "store/slices/dialog.store";
import { setPlannedDate, updatePlannedDate } from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { JobEvent } from "../calendarEvents/JobEvent";

luxonTimezone.luxon = luxon;
luxonTimezone.version = 3;

const PREFIX = "JobCalendarView";

const classes = {
  root: `${PREFIX}-root`,
  navHeader: `${PREFIX}-navHeader`,
  navSection: `${PREFIX}-navSection`,
};

const Root = styled("div")(() => ({
  [`& .${classes.root}`]: {
    width: "100%",
    flex: 1,
    position: "relative",
  },
  [`& .${classes.navHeader}`]: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
    flexBasis: "100%",
  },
  [`& .${classes.navSection}`]: {
    display: "inline-flex",
    alignItems: "center",
  },
}));
interface JobCalendarViewProps {
  jobs: ServiceJob[];
  toLink?: string;
  loading?: boolean;
  disabled?: boolean;
  refetchJobs?: any;
  plannerMode?: boolean;
  showAvailability?: boolean;
  showSalesTasks?: boolean;
  showProjectTasks?: boolean;
}

export const JobCalendarView: FC<JobCalendarViewProps> = (props) => {
  const {
    jobs,
    toLink,
    loading = false,
    refetchJobs,
    plannerMode,
    showAvailability = false,
    showSalesTasks = false,
    showProjectTasks = false,
  } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { languageVar } = useAppSelector((state) => state.user);
  const {
    selectedDate: lastSelectedDate,
    view: scheduleView,
    hideWeekends,
    mobileCalendar: { availability, salesTasks, projectTasks },
  } = useAppSelector((state) => state.calendar);
  const changePlannedDateEnabled = !plannerMode;
  const [selectedDate, setSelectedDate] = useState<DateType>(lastSelectedDate ?? new Date());
  const [isOpen, setPopupOpen] = useState(false);
  const [searchInput, setSearchInput] = useState<HTMLElement>();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [listEvents, setListEvents] = useState<MbscCalendarEvent[]>([]);

  const jobLookup: { [key: string]: MbscCalendarEvent } = {};
  jobs.forEach((job) => {
    jobLookup[job.id!] = { ...job };
  });

  const events = useMemo<MbscCalendarEvent[]>(() => {
    var ret: MbscCalendarEvent[] = jobs
      .filter((job) => job.plannedDate?.startTime || job.responseDate)
      .map((job) => {
        const dateText = job.plannedDate?.startTime || job.responseDate;
        const urgencyColor = calculateUrgencyColorHex(dateText);

        let start = job.plannedDate?.startTime || job.responseDate;
        let end = job.plannedDate?.stopTime;
        if ((end === null || end === undefined) && job.responseDate) {
          end = addHours(new Date(job.responseDate), 2).toISOString();
        }

        let event: MbscCalendarEvent = {
          id: job.id || undefined,
          title: job.externalId || undefined,
          color: urgencyColor,
          job: job,
          start: start ? new Date(start).toISOString() : undefined,
          end: end ? new Date(end).toISOString() : undefined,
        };

        return event;
      });

    if (showAvailability && availability && availability.length > 0) {
      ret.push(
        ...availability.map((a) => {
          let event: MbscCalendarEvent = {
            id: a.availNo || undefined,
            title: a.barText || undefined,
            color: a.color ?? undefined,
            start: a.dateFrom ? new Date(a.dateFrom).toISOString() : undefined,
            end: a.dateTo ? new Date(a.dateTo).toISOString() : undefined,
            availability: a,
          };
          return event;
        })
      );
    }

    if (showProjectTasks && projectTasks && projectTasks.length > 0) {
      ret.push(
        ...projectTasks
          .filter((p) => !jobs.some((j) => j.externalId === p.externalId))
          .map((p) => {
            let event: MbscCalendarEvent = {
              id: p.itemGuid || undefined,
              title: `${p.projectNo}/${p.taskNo}` || undefined,
              color: "lightgrey",
              start: p.dateFrom ? new Date(p.dateFrom).toISOString() : undefined,
              end: p.dateTo ? new Date(p.dateTo).toISOString() : undefined,
              projectTask: p,
              cellCssClass: "disabled-event",
            };
            return event;
          })
      );
    }

    if (showSalesTasks && salesTasks && salesTasks.length > 0) {
      ret.push(
        ...salesTasks.map((s) => {
          let event: MbscCalendarEvent = {
            id: s.taskText || undefined,
            title: s.taskSubject || undefined,
            color: undefined,
            start: s.dateFrom ? new Date(s.dateFrom).toISOString() : undefined,
            end: s.dateTo ? new Date(s.dateTo).toISOString() : undefined,
            salesTask: s,
          };
          return event;
        })
      );
    }
    return ret;
  }, [
    availability,
    projectTasks,
    jobs,
    salesTasks,
    showAvailability,
    showProjectTasks,
    showSalesTasks,
  ]);

  const invalidDates = [
    {
      recurring: {
        repeat: "weekly",
        weekDays: "SA,SU",
      },
    },
  ];

  const view = useMemo<MbscEventcalendarView>(() => {
    if (scheduleView === "month") {
      return {
        calendar: { type: "month", labels: true },
        agenda: { type: "month" },
      };
    } else {
      return {
        schedule: {
          type: scheduleView,
          startDay: 1,
          endDay: hideWeekends ? 5 : 0,
          startTime: engineerSettings?.workTimeStart,
          endTime: engineerSettings?.workTimeEnd,
          allDay: false,
        },
      };
    }
  }, [scheduleView, hideWeekends]);
  const listView = useMemo<MbscEventcalendarView>(
    () => ({
      agenda: {
        type: "year",
        size: 5,
      },
    }),
    []
  );

  const i18nLangToCalendarLocaleMap: { [key: string]: MbscLocale } = {
    de: localeDe,
    en: localeEn,
    es: localeEs,
    fr: localeFr,
    nn: localeNo,
    no: localeNo,
    nb: localeNo,
    sv: localeSv,
    uk: localeUa,
    ua: localeUa,
  };
  const calendarLocale = i18nLangToCalendarLocaleMap[languageVar];

  const searchInputRef = useCallback((input: Input) => {
    setSearchInput(input && input.nativeElement);
  }, []);
  const handleInputChange = useCallback(
    (ev: any) => {
      const text = ev.target.value;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        if (text.length > 0) {
          setListEvents(
            events.filter((x) => {
              return (
                x.job &&
                (x.title?.toLowerCase().includes(text.toLowerCase()) ||
                  x.job?.customer?.name?.toLowerCase().includes(text.toLowerCase()))
              );
            })
          );
          setPopupOpen(true);
        } else {
          setPopupOpen(false);
        }
      }, 200);
    },
    [events]
  );
  const handleInputFocus = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
    if (ev.target.value.length > 0) {
      setPopupOpen(true);
    }
  }, []);
  const renderHeader = useCallback(
    () => (
      <div className={classes.navHeader}>
        <div className={classes.navSection}>
          <CalendarNav />
          <IconButton
            color="info"
            aria-label={intl.formatMessage({ id: "general.refreshJobList" })}
            title={intl.formatMessage({ id: "general.refreshJobList" })}
            onClick={() => {
              refetchJobs(true);
            }}
            data-testid="JobCalendarView-RefreshButton"
          >
            <EventRepeatIcon />
          </IconButton>
        </div>
        <div className="sm-seach-header-bar">
          <Input
            startIcon="material-search"
            ref={searchInputRef}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            inputStyle="box"
            placeholder={intl.formatMessage({ id: "calendar.search" })}
            theme="material"
            themeVariant="light"
          />
        </div>
        <div className={classes.navSection}>
          <CalendarPrev />
          <CalendarToday />
          <CalendarNext />
        </div>
      </div>
    ),
    [handleInputChange, handleInputFocus, intl, refetchJobs, searchInputRef]
  );

  const renderEvent = useCallback(
    (data: MbscCalendarEventData) => {
      return (
        <>
          {data.original && data.original.job && (
            <JobEvent job={data.original.job} showEngineerName={plannerMode} />
          )}
          {data.original && data.original.availability && (
            <AvailabilityEvent availability={data.original.availability} />
          )}
          {data.original && data.original.salesTask && (
            <SalesTaskEvent salesTask={data.original.salesTask} />
          )}
          {data.original && data.original.projectTask && (
            <ProjectTaskEvent projectTask={data.original.projectTask} />
          )}
        </>
      );
    },
    [plannerMode]
  );

  const renderPopupEvent = useCallback(
    (data: MbscCalendarEventData) => {
      return (
        <>
          {data.original && data.original.job && (
            <JobEvent job={data.original.job} hideTimes showEngineerName={plannerMode} />
          )}
          {data.original && data.original.availability && (
            <AvailabilityEvent availability={data.original.availability} hideTimes />
          )}
          {data.original && data.original.salesTask && (
            <SalesTaskEvent salesTask={data.original.salesTask} hideTimes />
          )}
        </>
      );
    },
    [plannerMode]
  );

  const updatePlannedDateCb = useCallback(
    (jobId: string, plannedTimes: TimesType) => {
      dispatch(updatePlannedDate({ jobId: jobId, times: plannedTimes }))
        .then(unwrapResult)
        .then(({ queued }) => {
          if (queued) {
            dispatch(addSnackbarMessage({ key: "UpdatePlannedDate-stored" }));
          } else {
            dispatch(addSnackbarMessage({ key: "UpdatePlannedDate-success" }));
          }
          dispatch(setPlannedDate({ jobId: jobId, times: plannedTimes }));
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          dispatch(addSnackbarMessage({ key: "UpdatePlannedDate-fail" }));
        });
    },
    [dispatch]
  );

  const updateAvailabilityCb = useCallback(
    (availability: TechnicianAvailabilityType, dateFrom: DefinedDate, dateTo: DefinedDate) => {
      const request: UpdateAvailabilityInputType = {
        availNo: availability.availNo,
        availabilityType: availability.availabilityType,
        dateFrom: new Date(dateFrom).toISOString(),
        dateTo: new Date(dateTo).toISOString(),
        description: availability.barText,
      };
      dispatch(updateAvailability({ availability: request })).then(() => {
        dispatch(getMobileCalendar());
      });
    },
    [dispatch]
  );

  const dispatchSelectedDate = useCallback(
    (date: DateType) => {
      if (typeof date === "object") {
        dispatch(setCalendarSelectedDate({ date: formatDate(date as Date) }));
      } else {
        dispatch(setCalendarSelectedDate({ date: formatDate(date) }));
      }
    },
    [dispatch]
  );

  return (
    <Root>
      {loading && <LinearProgress />}
      <Eventcalendar
        theme="material"
        themeVariant="light"
        locale={calendarLocale}
        view={view}
        invalid={hideWeekends ? invalidDates : undefined}
        data={events}
        selectedDate={selectedDate}
        immutableData={true}
        dragToMove={changePlannedDateEnabled}
        dragToResize={changePlannedDateEnabled}
        dragTimeStep={15}
        timezonePlugin={luxonTimezone}
        dataTimezone="utc"
        displayTimezone="local"
        onSelectedDateChange={(event) => {
          setSelectedDate(event.date);
          dispatchSelectedDate(event.date);
        }}
        onRightClick={function (event: any) {
          event.domEvent.preventDefault();
        }}
        onCellClick={function (event) {
          event.domEvent.preventDefault();
        }}
        onLabelClick={function (event) {
          event.domEvent.preventDefault();
        }}
        onCellRightClick={function (event) {
          event.domEvent.preventDefault();
        }}
        onEventRightClick={function (event) {
          event.domEvent.preventDefault();
        }}
        onEventUpdate={(event) => {
          event.domEvent.preventDefault();

          const newEvent = event.event;
          let start;
          let end;

          if (newEvent.start instanceof Date) {
            start = newEvent.start.toISOString();
          } else if (typeof newEvent.start === "string") {
            start = newEvent.start;
          }

          if (newEvent.end instanceof Date) {
            end = newEvent.end.toISOString();
          } else if (typeof newEvent.end === "string") {
            end = newEvent.end;
          }

          if (start && end) {
            if (newEvent.job) {
              const plannedTimes: TimesType = {
                startTime: new Date(start).toISOString(),
                stopTime: new Date(end).toISOString(),
              };
              updatePlannedDateCb(newEvent.job.id, plannedTimes);
            }
            if (newEvent.availability) {
              updateAvailabilityCb(newEvent.availability, start, end);
            }
          }
        }}
        onEventClick={async (event) => {
          event.domEvent.preventDefault();
          if (event.event.job) navigate(`${toLink}/${event.event.id}`);
          if (event.event.availability) {
            await dispatch(setEditAvailability({ availability: event.event.availability }));
            dispatch(setOpen({ dialogName: "availability", open: true }));
          }
        }}
        renderHeader={renderHeader}
        renderEventContent={renderEvent}
        renderScheduleEventContent={renderEvent}
      />
      <Popup
        className="md-search-popup"
        display="anchored"
        showArrow={false}
        showOverlay={false}
        scrollLock={false}
        contentPadding={false}
        focusOnOpen={false}
        focusOnClose={false}
        anchor={searchInput}
        focusElm={searchInput}
        isOpen={isOpen}
        onClose={() => setPopupOpen(false)}
      >
        <Eventcalendar
          className="mbsc-popover-list"
          view={listView}
          data={listEvents}
          showControls={false}
          timezonePlugin={luxonTimezone}
          dataTimezone="utc"
          displayTimezone="local"
          onEventClick={(event) => {
            event.domEvent.preventDefault();
            dispatchSelectedDate(event.event.start!);
            navigate(`${toLink}/${event.event.id}`);
          }}
          renderEventContent={renderPopupEvent}
        />
      </Popup>
    </Root>
  );
};
